// @flow
import * as React from 'react';
import urlUtil from 'app/common/lib/urlUtil';
import withViewer from 'app/common/lib/react/withViewer';
import withVisibilityChanged from 'app/common/lib/react/withVisibilityChanged';
import { track } from 'app/common/lib/react/analytics';

const canShowLogStatements =
  urlUtil.getQueryStringParams() && urlUtil.getQueryStringParams().debug_impression_log;
const impressionItems = {
  Article: {
    impressionType: 'articleImpressions',
    idType: 'articleIdStr',
    eventType: PCONST.thrift.EventType.ARTICLE_IMPRESSION_ONE_PIXEL,
  },
  Pin: {
    impressionType: 'pinImpressions',
    idType: 'pinIdStr',
    eventType: PCONST.thrift.EventType.PIN_IMPRESSION_ONE_PIXEL,
  },
  Board: {
    impressionType: 'boardImpressions',
    idType: 'boardIdStr',
    eventType: PCONST.thrift.EventType.BOARD_IMPRESSION_ONE_PIXEL,
  },
  Interest: {
    impressionType: 'topicImpressions',
    idType: 'topicIdStr',
    eventType: PCONST.thrift.EventType.TOPIC_IMPRESSION_ONE_PIXEL,
  },
  Search: {
    impressionType: 'searchImpressions',
    idType: 'term',
    eventType: PCONST.thrift.EventType.SEARCH_IMPRESSION_ONE_PIXEL,
  },
  User: {
    impressionType: 'userImpressions',
    idType: 'userIdStr',
    eventType: PCONST.thrift.EventType.USER_IMPRESSION_ONE_PIXEL,
  },
  News: {
    impressionType: 'newsHubData',
    idType: 'newsIdStr',
    eventType: PCONST.thrift.EventType.NEWS_HUB_FEED_IMPRESSION_ONE_PIXEL,
  },
  Story: {
    impressionType: 'storyImpressions',
    idType: 'storyIdStr',
    eventType: PCONST.thrift.EventType.STORY_IMPRESSION_ONE_PIXEL,
  },
  Guide: {
    impressionType: 'guideImpressions',
    idType: 'term',
    eventType: PCONST.thrift.EventType.GUIDE_IMPRESSION_ONE_PIXEL,
  },
};
const NANOSECOND_MULTIPLIER = 1000000;

type Props = {|
  componentType?: number,
  contextLogData?: {},
  impressionAuxFields?: {
    richTypes?: string,
    imageURL?: string,
    videoIdStr?: ?string,
    clientTrackingParams?: string,
  },
  impressionType: 'Article' | 'Pin' | 'Interest' | 'Search' | 'User' | 'News' | 'Story' | 'Guide',
  loggingId: string,
  slotIndex: number,
  viewParameter: number,
  viewType: number,
  viewData: {
    interest?: string,
  },
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  viewer: $Subtype<{
    isEmployee?: boolean,
  }>,
|};

type State = {|
  impressionStarted: boolean,
  impressionLogged: boolean,
|};

/**
 * High order Function which consumes a Component,
 * and logs the corresponding impression when said component
 * enters and leaves the viewport.
 */
export default function withImpressionLogging(
  Subject: React.ComponentType<any>,
): React.ComponentType<any> {
  const SubjectWithVisibility = withVisibilityChanged(Subject);

  class ImpressionLogging extends React.Component<Props, State> {
    state: State = {
      impressionStarted: false,
      impressionLogged: false,
    };

    currentNs = () => Date.now() * NANOSECOND_MULTIPLIER;

    startTime = 0;

    debugPinImpressions =
      canShowLogStatements && this.props.impressionType === 'Pin' && this.props.viewer.isEmployee;

    handleVisibilityChanged = (visible: boolean) => {
      const {
        componentType,
        contextLogData,
        impressionAuxFields,
        impressionType,
        loggingId,
        slotIndex,
        viewParameter,
        viewType,
        viewData,
      } = this.props;

      if (visible) {
        // Item entering the dom
        this.startTime = this.currentNs();

        if (this.debugPinImpressions) {
          // eslint-disable-next-line no-console
          console.log(slotIndex + ' started timer');
          this.setState({ impressionStarted: true });
        }
      } else {
        // Item leaving the dom
        const impressionItem = impressionItems[impressionType];
        const idTypeKey = impressionItem.idType;

        const endTime = this.currentNs();
        const impression = {
          endTime,
          [idTypeKey]: loggingId,
          slotIndex,
          time: this.startTime,
          renderDuration: endTime - this.startTime,
          ...impressionAuxFields,
        };
        track(impressionItem.eventType, {
          component: componentType,
          eventData: {
            [impressionItem.impressionType]: [impression],
          },
          view: viewType,
          viewParameter,
          viewData,
          // IMPORTANT: aux fields need to be whitelisted in event_aux_data_whitelist.py
          ...contextLogData,
        });

        if (this.debugPinImpressions) {
          // eslint-disable-next-line no-console
          console.log(slotIndex + ' logged for ' + (endTime - this.startTime) / 1000000000 + 's');
          this.setState({ impressionStarted: false, impressionLogged: true });
        }
      }
    };

    render() {
      return (
        <SubjectWithVisibility
          onVisibilityChanged={this.handleVisibilityChanged}
          debugImpressionStarted={
            this.debugPinImpressions ? this.state.impressionStarted : undefined
          }
          debugImpressionLogged={this.debugPinImpressions ? this.state.impressionLogged : undefined}
          {...this.props}
        />
      );
    }
  }

  return withViewer(ImpressionLogging);
}
